import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Page visibility — the single source of truth for "which pages does this user see".
 *
 * Before this module the app had three divergent tab-key vocabularies and a broken
 * pipe: the Admin → Permissions matrix wrote to Zero.Config's `tab_permissions`,
 * while the sidebar read `profile.allowedTabs` from Zero.Auth — computed from a
 * JSONB column that NOTHING ever writes, so it always fell through to a hardcoded
 * default list. Editing permissions therefore had no effect on the actual nav.
 *
 * Everything now reads Zero.Config's matrix (the thing the Admin UI actually
 * writes). `profile.allowedTabs` is no longer consulted for page visibility.
 *
 * This is a UX-level gate, exactly as the per-route guards already were: the real
 * authority is each service's own role check on the endpoints behind a page.
 *
 * Note the matrix is per-CLUB (tenant-scoped), so unticking a page affects that
 * role only within the club whose settings you edited.
 */

/** Canonical page list — keys MUST match Layout's COCKPIT_NAV keys and the
 *  backend seed in Zero.Config's AdminSettingsService.DefaultTabPermissions. */
/**
 * Nav keys paired with their paths, IN SIDEBAR ORDER — the order a user sees in the rail.
 *
 * Used to pick a landing page ("/" → the first page you can actually open) and a refusal
 * destination. It lives here, not in routeTree.tsx, because it was previously declared there
 * AND copied into the test that claimed to mirror it — so a reorder updated one and left the
 * other silently asserting the old behaviour. One definition, imported by both.
 *
 * Keep in step with COCKPIT_NAV in components/Layout.tsx. Admin is deliberately absent: it is
 * the terminal fallback in firstVisiblePath() because every role can see it (it hosts the
 * per-user Preferences section), which is what guarantees the search terminates.
 */
export const TAB_PATHS: ReadonlyArray<readonly [string, string]> = [
  // First, and therefore the landing page after sign-in for every role that may open it (2026-09-29)
  // — unless the club chose another start page for the role (Admin → Permissions; see startPageOf).
  ['dashboard', '/dashboard'],
  // The session overview (2026-10): a fitness coach's "open the software and see the session".
  ['sessionOverview', '/session-overview'],
  ['reports', '/reports'],
  ['promptLab', '/prompt-lab'],
  ['dataroom', '/data-room'],
  ['live', '/live'],
  ['loadrisk', '/load-risk'],
  ['scouting', '/opponent-lab'],
  ['injuries', '/injuries'],
  ['passport', '/passport'],
  ['integration', '/integration'],
];

export const APP_TABS: { key: string; label: string }[] = [
  { key: 'loadrisk', label: 'Load & Risk' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'sessionOverview', label: 'Session overview' },
  { key: 'live', label: 'Live' },
  { key: 'dataroom', label: 'Data Room' },
  { key: 'reports', label: 'Reports' },
  { key: 'injuries', label: 'Injury Registry' },
  { key: 'scouting', label: 'Opponent Lab' },
  { key: 'promptLab', label: 'Prompt Lab' },
  { key: 'passport', label: 'Passport' },
  { key: 'integration', label: 'Integration' },
  { key: 'logs', label: 'Logs' },
  { key: 'admin', label: 'Admin' },
];

const ALL_TAB_KEYS = APP_TABS.map((t) => t.key);

/** Fallback matrix used only when Zero.Config has never been seeded for a tenant
 *  or the request fails — mirrors the backend defaults so behavior matches. */
export const DEFAULT_TAB_PERMISSIONS: Record<string, string[]> = {
  admin: [...ALL_TAB_KEYS],
  coach: [
    'loadrisk',
    'dashboard',
    'sessionOverview',
    'live',
    'reports',
    'injuries',
    'scouting',
    'promptLab',
  ],
  fitness_trainer: [
    'loadrisk',
    'dashboard',
    'sessionOverview',
    'live',
    'dataroom',
    'reports',
    'injuries',
  ],
  physio: ['loadrisk', 'dashboard', 'injuries'],
  analyst: ['dashboard', 'sessionOverview', 'live', 'dataroom', 'reports', 'scouting', 'promptLab'],
  // A player is not a staff user with fewer tabs — they belong on their own
  // profile page (/player) and nowhere else. Containment is the appGate redirect
  // plus the gateway's player lockdown; a page ticked here would only mislead.
  player_view: [],
  stakeholder: ['dashboard', 'sessionOverview', 'reports'],
};

export type TabPermissionMatrix = Record<string, string[]>;

export const TAB_PERMISSIONS_QUERY_KEY = ['tab-permissions'] as const;
export const MY_PERMISSIONS_QUERY_KEY = ['my-permissions'] as const;
export const TAB_PERMISSIONS_STALE_TIME = 60_000;

/** What the Admin → Permissions editor holds: every role's pages, and each role's start page. */
export interface TabPermissionsEditorData {
  matrix: TabPermissionMatrix;
  /** Role → the page it opens on after sign-in. A role with no entry uses the default. */
  startPages: Record<string, string>;
}

/**
 * The FULL matrix, every role, with every role's start page. Admin or super_admin only.
 *
 * Its one legitimate consumer is the Admin → Permissions editor, which has to render every role to
 * let an admin edit them. Everything else wants fetchMyPermissions below.
 */
export async function fetchTabPermissions(): Promise<TabPermissionsEditorData> {
  const { data } = await api.get('/admin/tab-permissions');
  return {
    matrix: (data?.permissions as TabPermissionMatrix) ?? {},
    startPages: (data?.startPages as Record<string, string> | undefined) ?? {},
  };
}

/**
 * The page a role OPENS on after sign-in (Admin → Permissions, 2026-10), riding on the caller's own
 * matrix under an entry no role can be named. That keeps the landing redirect on the one request it
 * already makes, and every reader of the matrix unchanged (they look up `matrix[role]` only).
 */
const START_PAGE_ENTRY = '$startPage';

/** The caller's start page from their own matrix, or null for the default (the first nav page). */
export function startPageOf(matrix: TabPermissionMatrix | undefined): string | null {
  return matrix?.[START_PAGE_ENTRY]?.[0] ?? null;
}

/**
 * The caller's OWN row, shaped as a single-entry matrix so `canSeeTab` is unchanged.
 *
 * The sidebar and the route guards only ever read `matrix[myRole]`, but they used to get here
 * through `/admin/tab-permissions` — so every logged-in user called an admin route and was handed
 * every other role's configuration. Disclosure nobody asked for, and it made an ordinary
 * page-visibility read look like an admin action in the network tab.
 *
 * Keyed by the role the SERVER reports, not by the profile's, so the two can never disagree about
 * whose permissions these are.
 */
export async function fetchMyPermissions(): Promise<TabPermissionMatrix> {
  const { data } = await api.get('/config/my-permissions');
  const role = data?.role as string | undefined;
  const permissions = (data?.permissions as string[] | undefined) ?? [];
  const startPage = data?.startPage as string | null | undefined;
  if (!role) return {};
  return startPage
    ? { [role]: permissions, [START_PAGE_ENTRY]: [startPage] }
    : { [role]: permissions };
}

/** Route-guard entry point (mirrors ensureProfile): cached when fresh, else one
 *  fetch. Never throws — a failed read degrades to the defaults so a Zero.Config
 *  blip cannot lock a user out of the whole app. */
export async function ensureTabPermissions(queryClient: QueryClient): Promise<TabPermissionMatrix> {
  try {
    return await queryClient.ensureQueryData({
      queryKey: MY_PERMISSIONS_QUERY_KEY,
      queryFn: fetchMyPermissions,
      staleTime: TAB_PERMISSIONS_STALE_TIME,
      retry: 1,
      retryDelay: 2_000,
    });
  } catch {
    return DEFAULT_TAB_PERMISSIONS;
  }
}

/**
 * The full matrix, for the Admin → Permissions editor only. Pass `enabled: false` when the viewer
 * is not an admin — the endpoint 403s for them, and firing it anyway puts a red row in the network
 * tab on a page non-admins legitimately open for Preferences.
 */
export function useTabPermissions(enabled = true) {
  return useQuery({
    queryKey: TAB_PERMISSIONS_QUERY_KEY,
    queryFn: fetchTabPermissions,
    staleTime: TAB_PERMISSIONS_STALE_TIME,
    enabled,
  });
}

/** The caller's own permissions — what the sidebar and every route guard use. */
export function useMyPermissions() {
  return useQuery({
    queryKey: MY_PERMISSIONS_QUERY_KEY,
    queryFn: fetchMyPermissions,
    staleTime: TAB_PERMISSIONS_STALE_TIME,
  });
}

/**
 * The visibility rule:
 *   super_admin   → sees everything (platform owner; not gated by the club's matrix)
 *   everyone else → visible when their role's own checkbox is ticked
 *
 * To hide pages for a demo, untick them for the role the audience will be using —
 * that is what the per-role matrix is for, and it now actually reaches the sidebar.
 */
export function canSeeTab(
  tabKey: string,
  role: string | undefined,
  matrix: TabPermissionMatrix | undefined,
): boolean {
  if (role === 'super_admin') return true;
  if (!role || role === 'pending') return false;

  const effective = matrix ?? DEFAULT_TAB_PERMISSIONS;
  const forRole = effective[role] ?? DEFAULT_TAB_PERMISSIONS[role] ?? [];
  return forRole.includes('*') || forRole.includes(tabKey);
}
